import Typography from "@/elements/Typography";
import Card from "@mui/material/Card";
import Box from "@/elements/Box";
import {
  IS_NEW_KEY,
  useDbRelationsUpdateForm,
} from "@/utils/useDbRelationsUpdate";
import Table from "@/elements/Table";
import {
  DescrizioneBonificaDto,
  getGetDescrizioniBonificaQueryKey,
  useUpdateDescrizioniBonifica,
} from "@/api";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createColumnHelper, Row } from "@tanstack/react-table";
import Button from "@/elements/Button";
import { FormHelperText, Icon } from "@mui/material";
import { Controller } from "react-hook-form";
import Input from "@/elements/Input";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";

const columnHelper = createColumnHelper<DescrizioneBonificaDto>();

export default function DescrizioniBonificaTableCard({
  descrizioniBonifiche,
  canEdit,
}: {
  descrizioniBonifiche: DescrizioneBonificaDto[];
  canEdit: boolean;
}) {
  const { t } = useTranslation();

  const { mutateAsync, isLoading: isSaving } = useUpdateDescrizioniBonifica({
    mutation: {
      onSuccess: () => {
        toast.success(t("descrizioniBonificaTableCard.toast.salvate"));
      },
      onError: () => {
        toast.error(t("descrizioniBonificaTableCard.toast.errore"));
      },
    },
  });

  const {
    data,
    control,
    handleSubmit,
    getChangeSubmitData,
    create,
    remove,
    restore,
    getFieldName,
    getEntityState,
  } = useDbRelationsUpdateForm(descrizioniBonifiche, {
    deleteChangeType: "deleted",
  });

  const editing = canEdit;

  const columns = useMemo(() => {
    const columns = [
      columnHelper.accessor("descrizione", {
        header: t("descrizioniBonificaTableCard.colonne.descrizione"),
        cell: editing
          ? ({ row }) => (
              <Controller
                name={getFieldName(row.index, "descrizione")}
                control={control}
                rules={{
                  required: t("descrizioniBonificaTableCard.errori.descrizione"),
                }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <>
                      <Input {...field} />
                      {error && (
                        <FormHelperText error>{error?.message}</FormHelperText>
                      )}
                    </>
                  );
                }}
              />
            )
          : ({ getValue }) => getValue() || null,
      }),
      columnHelper.accessor("descrizioneEn", {
        header: "Descrizione (EN)",
        cell: editing
          ? ({ row }) => (
              <Controller
                name={getFieldName(row.index, "descrizioneEn")}
                control={control}
                rules={{ required: "Inserisci la descrizione in inglese" }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <>
                      <Input {...field} />
                      {error && (
                        <FormHelperText error>{error?.message}</FormHelperText>
                      )}
                    </>
                  );
                }}
              />
            )
          : ({ getValue }) => getValue() || null,
      }),
      columnHelper.accessor("descrizioneDe", {
        header: "Descrizione (DE)",
        cell: editing
          ? ({ row }) => (
              <Controller
                name={getFieldName(row.index, "descrizioneDe")}
                control={control}
                rules={{ required: "Inserisci la descrizione in tedesco" }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <>
                      <Input {...field} />
                      {error && (
                        <FormHelperText error>{error?.message}</FormHelperText>
                      )}
                    </>
                  );
                }}
              />
            )
          : ({ getValue }) => getValue() || null,
      }),
      editing &&
        columnHelper.display({
          id: "buttons",
          header: "",
          size: 125,
          maxSize: 125,
          cell: ({ row }) => {
            return (
              <>
                <Box display="flex" gap={1} flexDirection="row-reverse">
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<Icon>delete_outlined</Icon>}
                    onClick={() => {
                      remove(row.index);
                    }}
                  >
                    {t("azioni.elimina")}
                  </Button>
                </Box>
                <Box
                  display="flex"
                  gap={1}
                  flexDirection="row-reverse"
                  className="deleted-row-visible-content flex"
                >
                  <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    startIcon={<Icon>restore_from_trash</Icon>}
                    onClick={() => {
                      restore(row.index);
                    }}
                  >
                    {t("azioni.ripristina")}
                  </Button>
                </Box>
              </>
            );
          },
        }),
    ].filter((x) => x);
    return columns as Exclude<(typeof columns)[number], boolean>[];
  }, [editing, control, getFieldName, remove, restore, t]);

  const isRowDeleted = useCallback(
    (row: Row<DescrizioneBonificaDto>) => {
      return getEntityState(row.index) === "deleted";
    },
    [getEntityState]
  );

  const queryClient = useQueryClient();

  const onSubmit = async () => {
    const data = getChangeSubmitData();
    try {
      await mutateAsync({
        data,
      });
      queryClient
        .invalidateQueries({
          queryKey: getGetDescrizioniBonificaQueryKey(),
        })
        .then();
    } catch (err) {}
  };

  return (
    <Card sx={{ width: "100%" }}>
      <Box p={2} flex={1}>
        <Box display="flex" flexDirection="column" height="100%">
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" color="text" fontWeight="medium">
              {t("descrizioniBonificaTableCard.titolo")}
            </Typography>
            <Button
              size="small"
              color="primary"
              onClick={() => {
                create();
              }}
            >
              {t("descrizioniBonificaTableCard.azioni.aggiungi")}
            </Button>
          </Box>

          <Table
            columns={columns}
            data={data}
            sortable={false}
            initialSort={[
              { id: IS_NEW_KEY, desc: false },
              { id: "descrizione", desc: false },
            ]}
            isRowDeleted={isRowDeleted}
          />

          <Box display="flex" flexDirection="row-reverse">
            <Button
              size="small"
              color="primary"
              onClick={handleSubmit(onSubmit)}
              disabled={isSaving}
            >
              {t("azioni.salva")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Card>
  );
}
